import React, { useEffect, useRef, useState, useImperativeHandle } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetWeeklySchedule, useRescheduleAppointment } from '../../../hooks';
import { appointmentRescheduleSchema } from '../../../constants';
import { showError, showSuccess } from '../../../utils';
import 'react-datepicker/dist/react-datepicker.css';
import LeftIcon from '../../../assets/images/left-arw.png';
import RightIcon from '../../../assets/images/right-arw.png';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../atoms';
import { WheelTimePicker } from '../../atoms/time-picker/time-picker';
import moment from 'moment';

function convertToAbbreviation(fullDayName) {
  // Define an array mapping full day names to their abbreviated forms
  var daysMapping = {
    sunday: 'Sun',
    monday: 'Mon',
    tuesday: 'Tue',
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
  };

  // Return the abbreviated form of the day if it exists in the mapping, otherwise return the input
  return daysMapping[fullDayName] || fullDayName;
}

// Define the order of days of the week
const daysOfWeek = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const sortArrayByDayName = (data) => {
  // Create a mapping of dayName to its index in the daysOfWeek array
  const dayIndexMap = {};
  daysOfWeek.forEach((day, index) => {
    dayIndexMap[day] = index;
  });

  // Sort the array using a custom comparator function
  data.sort((a, b) => {
    const dayIndexA = dayIndexMap[a.shiftDayName?.toLowerCase()];
    const dayIndexB = dayIndexMap[b.shiftDayName?.toLowerCase()];
    return dayIndexA - dayIndexB;
  });

  return data;
};

const getCurrentWeekCount = (date) => {
  const now = new Date(date);

  const yearStart = new Date(now.getFullYear(), 0, 1);

  let startDayOfWeek = yearStart.getDay();

  startDayOfWeek = startDayOfWeek === 0 ? 7 : startDayOfWeek;

  const diff =
    (now - yearStart + (startDayOfWeek - 1) * 24 * 60 * 60 * 1000) /
    (24 * 60 * 60 * 1000);

  const weekCount = Math.floor(diff / 7) + 1;

  return weekCount;
};

const displayDate = (dateString) => {
  return moment(dateString).format('MMMM D, YYYY');
};

const buildStartTimeIso = ({ selectedDate, timeHHMM }) => {
  if (!selectedDate || !timeHHMM) return '';
  return moment(
    `${selectedDate} ${timeHHMM}`,
    'YYYY-MM-DD HH:mm'
  ).toISOString();
};

export const RescheduleAppointmentForm = ({
  setPage,
  selectedAppointment,
  setAppointmentData,
  closeRescheduleModal,
  setIsLoading,
  details,
  setAppointmentDetails,
  refetch,
  page,
  setFetchMoreAllowed,
}) => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const currentDate = format(new Date(), 'yyyy-MM-dd');
  const currentDte = new Date();
  const ninetyDaysLater = new Date();
  ninetyDaysLater.setDate(currentDte.getDate() + 90);
  const [scheduleData, setScheduleData] = useState([]);
  const handleRescheduleAppointment = useRescheduleAppointment();

  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [overrideMessage, setOverrideMessage] = useState('');
  const [pendingOverrideArgs, setPendingOverrideArgs] = useState(null);

  const storedData = sessionStorage.getItem('storefrontData');
  const parsedData = storedData ? JSON.parse(storedData) : null;
  const subscriptionExpiryDate = new Date(parsedData?.subscriptionExpiryDate);
  const futureBookingsEnabled = parsedData?.futureBookingsEnabled;

  const getMaxBookingDate = () => {
    if (futureBookingsEnabled) return ninetyDaysLater;

    return new Date(
      Math.min(subscriptionExpiryDate.getTime(), ninetyDaysLater.getTime())
    );
  };

  const initialCursorDate = selectedAppointment?.startTime
    ? moment(selectedAppointment.startTime).startOf('isoWeek')
    : moment().startOf('isoWeek');

  const [weekCursorDate, setWeekCursorDate] = useState(initialCursorDate);

  const [filter, setFilter] = useState({
    dateRange: {
      fromDate: moment(initialCursorDate)
        .startOf('isoWeek')
        .format('YYYY-MM-DD'),
      toDate: moment(initialCursorDate).endOf('isoWeek').format('YYYY-MM-DD'),
    },
  });

  const { data: weeklyData, loading } = useGetWeeklySchedule(filter);

  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), 'yyyy-MM-dd')
  );

  useImperativeHandle(ref, () => ({
    focus: () => ref.current.focus(), // Expose a focus function
  }));

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(appointmentRescheduleSchema),
    defaultValues: {
      time: '',
    },
  });

  const onSubmit = async (data) => {
    if (selectedDate === null) {
      return showError('Please select date');
    }

    const startTime = buildStartTimeIso({
      selectedDate,
      timeHHMM: data.time,
    });

    if (!startTime) {
      return showError('Please select time');
    }

    setIsLoading?.(true);
    if (page !== 1) setPage?.(1);
    const payload = {
      appointmentUid: selectedAppointment?.uid,
      input: {
        startTime,
      },
      allowUnbookableTime: false,
    };
    const response = await handleRescheduleAppointment(payload);
    if (response?.data?.rescheduleAppointment) {
      if (details) {
        setAppointmentDetails(response?.data?.rescheduleAppointment);
        navigate(location.pathname, {
          replace: true,
          state: response?.data?.rescheduleAppointment,
        });
        showSuccess('Appointment rescheduled successfully');
        closeRescheduleModal();
        setIsLoading?.(false);
        reset();
        return;
      }
      setFetchMoreAllowed(false);
      setAppointmentData([]);
      closeRescheduleModal();
      refetch();
      showSuccess('Appointment rescheduled successfully');
      setIsLoading?.(false);
    } else {
      // Displaying the error message
      setIsLoading?.(false);
      const message = typeof response === 'string' ? response : '';
      const isOverrideCandidate =
        message.includes('Selected slots are not enough') ||
        message.includes('Slots are already being booked') ||
        message.includes("Appointment can't be booked for holiday");

      if (isOverrideCandidate) {
        setOverrideMessage(message);
        setPendingOverrideArgs({
          appointmentUid: selectedAppointment?.uid,
          input: {
            startTime,
          },
        });
        setOverrideModalOpen(true);
        return;
      }

      showError(response);
    }
  };

  const closeOverrideModal = () => {
    setOverrideModalOpen(false);
    setOverrideMessage('');
    setPendingOverrideArgs(null);
  };

  const confirmOverride = async () => {
    if (!pendingOverrideArgs) return;

    setIsLoading?.(true);
    try {
      const response = await handleRescheduleAppointment({
        ...pendingOverrideArgs,
        allowUnbookableTime: true,
      });

      if (response?.data?.rescheduleAppointment) {
        if (details) {
          setAppointmentDetails(response?.data?.rescheduleAppointment);
          navigate(location.pathname, {
            replace: true,
            state: response?.data?.rescheduleAppointment,
          });
          showSuccess('Appointment rescheduled successfully');
          closeRescheduleModal();
          setIsLoading?.(false);
          reset();
          closeOverrideModal();
          return;
        }
        setFetchMoreAllowed(false);
        setAppointmentData([]);
        closeRescheduleModal();
        refetch();
        showSuccess('Appointment rescheduled successfully');
        setIsLoading?.(false);
        closeOverrideModal();
        return;
      }

      setIsLoading?.(false);
      showError(response);
    } catch (e) {
      setIsLoading?.(false);
      showError('Failed to reschedule appointment');
    }
  };

  // Function to handle clicking on the left arrow
  const goToPreviousWeek = () => {
    setWeekCursorDate((prev) =>
      moment(prev).subtract(1, 'week').startOf('isoWeek')
    );
  };

  // Function to handle clicking on the right arrow
  const goToNextWeek = () => {
    setWeekCursorDate((prev) => moment(prev).add(1, 'week').startOf('isoWeek'));
  };

  const canGoToNextWeek = () => {
    const maxBookingDate = getMaxBookingDate();
    const nextWeekStart = moment(weekCursorDate)
      .add(1, 'week')
      .startOf('isoWeek');

    return !nextWeekStart.isAfter(
      moment(maxBookingDate).endOf('isoWeek'),
      'day'
    );
  };

  useEffect(() => {
    const startDate = moment(weekCursorDate).startOf('isoWeek');
    const endDate = moment(weekCursorDate).endOf('isoWeek');

    setFilter({
      dateRange: {
        fromDate: startDate.format('YYYY-MM-DD'),
        toDate: endDate.format('YYYY-MM-DD'),
      },
    });
  }, [weekCursorDate]);

  useEffect(() => {
    if (weeklyData) {
      const sortedData = sortArrayByDayName(weeklyData?.sellerScheduleStatuses);
      const currentDate = new Date();
      const ninetyDaysLater = new Date();
      ninetyDaysLater.setDate(currentDate.getDate() + 90); // Set the date to 90 days later

      setScheduleData(
        sortedData.map((item) => {
          const shiftDate = new Date(item.shiftDate);
          const newItem = { ...item };
          if (!futureBookingsEnabled) {
            newItem.enabled =
              shiftDate <= subscriptionExpiryDate &&
              shiftDate <= ninetyDaysLater;
          } else {
            newItem.enabled = shiftDate <= ninetyDaysLater;
          }
          return newItem;
        })
      );
    }
  }, [weeklyData, futureBookingsEnabled]);

  useEffect(() => {
    if (loading) {
      setIsLoading?.(true);
    } else {
      setIsLoading?.(false);
    }
  }, [loading]);

  const isCurrentWeekInSchedule = () => {
    const currentWeekStart = moment().startOf('isoWeek');
    const cursorWeekStart = moment(weekCursorDate).startOf('isoWeek');

    return cursorWeekStart.isSame(currentWeekStart, 'day');
  };

  useEffect(() => {
    const isCurrentDateEnabled = scheduleData.some(
      (item) => item.shiftDate === currentDate && item.enabled
    );
    if (selectedAppointment?.startTime) {
      return setSelectedDate(selectedAppointment?.startTime?.split('T')[0]);
    }
    if (isCurrentDateEnabled) {
      setSelectedDate(currentDate);
    } else {
      const nextEnabledDate = scheduleData.find(
        (item) => item.shiftDate > currentDate && item.enabled
      );
      if (nextEnabledDate) {
        setSelectedDate(nextEnabledDate.shiftDate);
      }
    }
  }, [currentDate, scheduleData, selectedAppointment]);

  return (
    <>
      {loading && <Spinner />}
      <form className="date-icon" onSubmit={handleSubmit(onSubmit)}>
        <div className="calander-box mt-8 md:w-full md:block">
          <div className="flex gap-4 justify-between items-center w-full">
            <p className="md:text-base text-xs font-semibold">
              {selectedDate ? displayDate(selectedDate) : 'Please Select Date'}
            </p>
            <div className="flex md:gap-4 gap-7">
              <img
                src={LeftIcon}
                alt="icon"
                className=" cursor-pointer"
                style={{ opacity: isCurrentWeekInSchedule() && 0.3 }}
                onClick={() => {
                  if (!isCurrentWeekInSchedule()) {
                    goToPreviousWeek();
                  }
                }}
              />
              {/* <img
                src={RightIcon}
                alt="icon"
                className={`cursor-pointer ${
                  futureBookingsEnabled
                    ? ''
                    : currentWeek >= expiryWeekCount
                    ? 'opacity-40 cursor-not-allowed'
                    : ''
                }`}
                onClick={() => {
                  if (futureBookingsEnabled) {
                    goToNextWeek();
                  } else {
                    if (currentWeek >= expiryWeekCount) {
                      return;
                    } else {
                      goToNextWeek();
                    }
                  }
                }}
              /> */}
              <img
                src={RightIcon}
                alt="icon"
                className={`cursor-pointer ${
                  canGoToNextWeek() ? '' : 'opacity-40 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (!canGoToNextWeek()) return;

                  goToNextWeek();
                }}
              />
            </div>
          </div>
          <div className="calendar-date flex md:gap-3 gap-1 flex-wrap items-center mt-3">
            {scheduleData?.map((item, index) => {
              const currentDate = format(new Date(), 'yyyy-MM-dd');
              const isFutureDate = item.shiftDate >= currentDate;
              return (
                <div
                  key={index}
                  onClick={() =>
                    isFutureDate && setSelectedDate(item?.shiftDate)
                  }
                  className={`grid border border-purple-bdr rounded-xl md:p-2 p-1 py-2 text-center md:w-45 w-9 ${
                    selectedDate === item?.shiftDate && item?.enabled
                      ? 'border-blue-bar bg-blue-calBg cursor-pointer'
                      : !item?.enabled || !isFutureDate
                      ? isFutureDate
                        ? 'opacity-60 hover:border-blue-bar hover:bg-blue-calBg cursor-pointer'
                        : 'opacity-40 cursor-not-allowed'
                      : 'hover:border-blue-bar hover:bg-blue-calBg cursor-pointer'
                  }`}
                >
                  <p className="text-purple-light text-xs">
                    {convertToAbbreviation(item?.shiftDayName)}
                  </p>
                  <p className="text-black-main font-semibold md:text-base text-sm">
                    {item?.shiftDate?.split('-')[2]}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
        <div className="mt-4">
          <label className="text-sm  text-gray-dark">Time:</label>
          <Controller
            name="time"
            control={control}
            defaultValue={
              selectedAppointment?.startTime
                ? moment(selectedAppointment.startTime).format('HH:mm')
                : ''
            }
            render={({ field }) => (
              <WheelTimePicker
                value={field.value}
                onChange={(val) => field.onChange(val)}
                placeholder="Select time"
              />
            )}
          />
          {errors && errors?.time && (
            <p className="text-red-drk md:text-r0.6875 text-r0.625 ">
              {errors?.time?.message}
            </p>
          )}
        </div>

        {overrideModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white-main w-11/12 md:w-96 rounded p-5 relative">
              <button
                type="button"
                className="absolute right-3 top-2 font-semibold"
                onClick={closeOverrideModal}
              >
                &#x2715;
              </button>
              <h3 className="font-semibold text-base">Schedule anyway?</h3>
              <p className="text-sm text-gray-dark mt-3">
                This time conflicts with your schedule (overlap, outside hours,
                or holiday).
              </p>
              {overrideMessage && (
                <p className="text-xs text-gray-middle mt-2 break-words">
                  {overrideMessage}
                </p>
              )}
              <div className="grid grid-cols-2 gap-3 mt-6">
                <button
                  type="button"
                  className="h-10 w-full border border-gray-dark bg-very-light text-gray-dark text-xs font-bold rounded-md"
                  onClick={closeOverrideModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="h-10 w-full bg-primary text-white-main text-xs font-bold rounded-md"
                  onClick={confirmOverride}
                >
                  Schedule anyway
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid mt-6">
          <button
            type="submit"
            className="h-10 w-full border  bg-primary text-white-main  text-xs hover:bg-blue-hover py-2 text-center font-bold rounded-md"
          >
            Book an appointment
          </button>
        </div>
      </form>
    </>
  );
};
